/**
 * The browser side of a push subscription: what this device supports, what it currently
 * holds, and how to start or stop.
 *
 * No React in here, so the awkward parts — feature detection across two very different
 * platforms, the key encoding, the order of operations when switching off — can be reasoned
 * about and tested on their own.
 */

/** Why notifications cannot be offered on this device, when they cannot. */
export type PushBlocker =
  /** iOS, in a Safari tab. Installing to the home screen is what unlocks it. */
  | 'needs-install'
  /** No Push API at all — an old browser, or an in-app webview. Nothing to suggest. */
  | 'unsupported'
  /** Asked before and refused. Only the browser's own settings can undo this. */
  | 'denied';

export interface PushSupport {
  /** Null when notifications can be offered here. */
  blocker: PushBlocker | null;
}

/**
 * Whether this browser can do Web Push, and if not, whether that is worth explaining.
 *
 * **The iOS case is the whole reason this returns a reason rather than a boolean.** On
 * iPhone the Push API exists *only* in a home-screen web app — in a Safari tab
 * `window.PushManager` is simply undefined. So the honest answer for somebody reading this
 * page in Safari is not "your browser cannot do this", it is "add it to your home screen
 * first", which is a thing they can act on and which the section directly below already
 * offers. Detecting iOS is what tells those two apart.
 */
export function detectPushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { blocker: 'unsupported' };
  }

  const hasApi =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  if (!hasApi) {
    return { blocker: isIosBrowser() && !isStandalone() ? 'needs-install' : 'unsupported' };
  }

  if (Notification.permission === 'denied') {
    return { blocker: 'denied' };
  }

  return { blocker: null };
}

/** Already running from the home screen? iOS still reports it the old, non-standard way. */
function isStandalone(): boolean {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone === true;

  return legacy || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * iPhone or iPad, in any browser.
 *
 * Broader than `installStore`'s check, which asks specifically whether *Safari* is showing
 * the Share sheet that can install. Here the question is only "is this the platform where
 * push needs an installed app", and the answer is the same in Chrome for iOS, because every
 * browser there renders with WebKit. iPadOS claims to be a Mac in its user agent, so touch
 * points are what give an iPad away.
 */
function isIosBrowser(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** A subscription flattened into the shape the API takes. */
export interface StoredSubscription {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/**
 * The browser's current subscription, if it has one.
 *
 * `serviceWorker.ready` rather than `getRegistration()`: a subscription can only be read
 * through an *active* worker, and on a cold start the worker is very often still
 * installing. Waiting is the difference between "notifications are off" and "notifications
 * are off for the first two seconds after every launch".
 */
export async function readSubscription(): Promise<StoredSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  return subscription === null ? null : toStored(subscription);
}

/**
 * Opens the browser's permission dialog.
 *
 * **Separate from {@link subscribe}, and called synchronously from the tap.** Raising this
 * prompt requires a user gesture, and iOS is the strict one: a request that is not clearly
 * a response to direct interaction is refused outright, with no dialog and no error worth
 * reading. Subscribing is several awaits deep — the capability query, the service worker
 * becoming ready — and putting any of them in front of this is how the button comes to do
 * nothing at all on an iPhone while working perfectly on Android.
 *
 * So the caller starts this first and hands the promise to `subscribe`. Harmless when
 * permission has already been granted: it resolves immediately and shows nothing.
 */
export function requestPermission(): Promise<NotificationPermission> {
  return Notification.requestPermission();
}

/**
 * Subscribes this device, once the permission dialog has been answered.
 *
 * Takes the promise from {@link requestPermission} rather than calling it, so that the
 * prompt belongs to the tap and everything slower happens behind it.
 */
export async function subscribe(
  publicKey: string,
  permission: Promise<NotificationPermission>,
): Promise<StoredSubscription> {
  if ((await permission) !== 'granted') {
    throw new Error('Notifications were not allowed.');
  }

  const registration = await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    // Not optional: Chrome refuses a subscription without it. It is a promise that every
    // push results in a visible notification — which `push-sw.js` keeps, on every path.
    userVisibleOnly: true,
    applicationServerKey: decodeKey(publicKey),
  });

  return toStored(subscription);
}

/**
 * Stops this device receiving pushes, and reports the endpoint that was cancelled so the
 * caller can tell the server.
 *
 * The local unsubscribe happens first on purpose. What the person asked for is "stop
 * buzzing this phone", and doing that is entirely local — it must not depend on the network
 * being up. The server call that follows is a tidy-up: if it never lands, the row is left
 * pointing at a dead endpoint and the push service answers 410 the next time, which is
 * exactly the signal the dispatcher already deletes on. The state self-heals in the
 * direction the person wanted.
 */
export async function unsubscribe(): Promise<string | null> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription === null) {
    return null;
  }

  const { endpoint } = subscription;
  await subscription.unsubscribe();

  return endpoint;
}

function toStored(subscription: PushSubscription): StoredSubscription {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};

  if (!keys.p256dh || !keys.auth) {
    // Has not been observed in a real browser, but the type allows it and a subscription
    // without keys cannot be encrypted to — better a clear error here than a 400 later.
    throw new Error('The browser returned a subscription with no keys.');
  }

  return { endpoint: subscription.endpoint, p256dh: keys.p256dh, auth: keys.auth };
}

/**
 * base64url to bytes.
 *
 * The spec allows `applicationServerKey` to be a base64url *string*, and Chrome accepts
 * one — but Safari has historically wanted a `BufferSource`, and passing bytes works
 * everywhere. This is not a place to be clever: a key the browser mis-parses produces a
 * subscription that looks fine and can never be pushed to, because the signature will not
 * match the key the subscription was created with.
 */
function decodeKey(base64Url: string) {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

  // Allocated by length rather than built with `Uint8Array.from`, which infers the wider
  // `Uint8Array<ArrayBufferLike>` — TypeScript 5.7 made these generic over their buffer,
  // and `BufferSource` accepts only the `ArrayBuffer`-backed form. `new Uint8Array(n)` is
  // the one that gives it.
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}
